'use client';

import { FC, PropsWithChildren, useEffect, useState } from 'react';
import { ButtonProps } from './types';
import clsx from 'clsx';

import styles from './BaseButton.module.css';
import Spinner from '@/components/Spinner';

const Button: FC<PropsWithChildren<ButtonProps>> = ({
  id,
  classes,
  loading,
  type = 'button',
  label,
  children,
  disabled,
  ariaLabel,
  onClick,
  formId,
  ...rest
}) => {
  const [rippleState, setRippleState] = useState<any>({ left: 0, top: 0 });

  const combinedClassName = clsx(styles.btn_root, styles.btn_root_transition, 'btnPrimary', classes, {
    ['btnDisabled']: disabled,
  });
  const isDisabled: boolean = loading !== undefined && loading;

  let timer: any = 0;
  const runRipple = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.stopPropagation();

    const { pageX, pageY, currentTarget } = ev;
    const rect = currentTarget.getBoundingClientRect();

    const left = pageX - (rect.left + window.scrollX);
    const top = pageY - (rect.top + window.scrollY);
    const rippleSize = Math.max(rect.width, rect.height);

    setRippleState((prevState: any) => ({
      ...prevState,
      left,
      top,
      opacity: 0.3,
      transform: 'translate(-50%, -50%)',
      transition: 'cubic-bezier(0.4, 0, 0.2, 1)',
      backgroundColor: '#9e9e9e',
    }));
    timer = setTimeout(() => {
      setRippleState((prevState: any) => ({
        ...prevState,
        opacity: 0,
        transform: `scale(${rippleSize / 9})`,
        transition: `all ${1000}ms`,
      }));
    }, 10);
  };

  useEffect(() => {
    return clearTimeout(timer);
  }, []);

  return (
    <button
      id={id}
      form={formId}
      data-testid={id}
      aria-label={ariaLabel || 'button'}
      onClick={(event) => {
        if (onClick) {
          onClick(event);
        }
        runRipple(event);
      }}
      disabled={isDisabled || disabled}
      className={combinedClassName}
      type={type}
      {...rest}
    >
      {typeof label === 'string' ? <span className={styles.labelDescription}>{label}</span> : label}
      {children}
      {!disabled && <span className={styles.rippleRoot} style={{ ...rippleState }}></span>}
      {loading && <Spinner />}
    </button>
  );
};

export default Button;
